import {
  bankGuaranteeFee,
  can,
  FIN_COST_SECTIONS,
  FIN_ROUTE,
  FIN_SECTIONS,
  FIN_STATUSES,
  type FinPlansResponse,
  type FinPlanView,
  type FinPlanVolumes,
  type FinRouteAction,
  type FinSection,
  finRoutePermission,
  type ProjectDetail,
  seesMoney,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { useCostArticles } from '../../lib/articles';
import { dateTime, percent, tengeShort } from '../../lib/format';
import { mln } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { FinPlanAmountInput } from './FinPlanAmountInput';
import { FinPlanBaselinePanel } from './FinPlanBaselinePanel';

export const finPlansQuery = (projectId: string, versionId?: string) =>
  queryOptions({
    queryKey: ['finplan', projectId, versionId ?? 'current'],
    queryFn: () => api<FinPlansResponse>(`/projects/${projectId}/fin-plans`, { query: { versionId } }),
  });

const STATUS_TONE: Record<string, Tone> = {
  draft: 'neutral',
  review_pto: 'info',
  review_fm: 'info',
  review_curator: 'warning',
  approved: 'success',
  superseded: 'neutral',
};

const BG_ARTICLE = 'Погашение процентов (вознаграждения) по БГ';
const SECTION_ORDER = Object.keys(FIN_SECTIONS) as FinSection[];
const monthFmt = new Intl.DateTimeFormat('ru-RU', { month: 'short', year: '2-digit' });
const monthTitle = (m: string) => monthFmt.format(new Date(`${m}-01T00:00:00`)).replace('.', '');

interface DraftRow {
  key: string;
  section: FinSection;
  article: string | null;
  name: string;
  values: Record<string, number>;
}

const toDraft = (plan: FinPlanView): DraftRow[] =>
  plan.rows.map((r) => ({
    key: r.id,
    section: r.section,
    article: r.article,
    name: r.name,
    values: { ...r.values },
  }));

function AddRowDialog({ onAdd, onClose }: { onAdd: (r: DraftRow) => void; onClose: () => void }) {
  const costArticles = useCostArticles();
  const [section, setSection] = useState<FinSection>('purchase');
  const [article, setArticle] = useState<string>(costArticles[0] ?? '');
  const [name, setName] = useState('');
  const isCost = FIN_COST_SECTIONS.includes(section);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Добавить строку"
      description="Статьи расходов — из утверждённых; новую статью запрашивают на странице «Статьи расходов» (БМ-15)."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!isCost && !name.trim()}
            onClick={() => {
              onAdd({
                key: `new-${Date.now()}`,
                section,
                article: isCost ? article : null,
                name: name.trim() || article,
                values: {},
              });
              onClose();
            }}
          >
            Добавить строку
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Раздел">
          <Select value={section} onChange={(e) => setSection(e.target.value as FinSection)}>
            {SECTION_ORDER.map((s) => (
              <option key={s} value={s}>
                {FIN_SECTIONS[s]}
              </option>
            ))}
          </Select>
        </Field>
        {isCost ? (
          <Field label="Статья расходов">
            <Select value={article} onChange={(e) => setArticle(e.target.value)}>
              {costArticles.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Наименование строки" hint={isCost ? 'Пусто — по названию статьи' : undefined}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например, этап 2: аванс 30 %"
          />
        </Field>
      </div>
    </Dialog>
  );
}

function ReturnDialog({
  onSend,
  onClose,
  pending,
}: {
  onSend: (c: string) => void;
  onClose: () => void;
  pending: boolean;
}) {
  const [c, setC] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Вернуть на доработку"
      description="Руководитель проекта увидит причину в карточке плана."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" loading={pending} disabled={!c.trim()} onClick={() => onSend(c.trim())}>
            Вернуть на доработку
          </Button>
        </>
      }
    >
      <Field label="Что доработать">
        <Textarea rows={4} value={c} onChange={(e) => setC(e.target.value)} />
      </Field>
    </Dialog>
  );
}

export function FinPlanSettings({
  plan,
  editable,
  hasUnsavedRows,
  onSaved,
}: {
  plan: FinPlanView;
  editable: boolean;
  hasUnsavedRows: boolean;
  onSaved: (r: FinPlansResponse) => void;
}) {
  const [f, setF] = useState({
    contractAmount: plan.contractAmount ? String(plan.contractAmount) : '',
    vatRate: String(plan.vatRate),
    financingRate: String(plan.financingRate),
    startMonth: plan.startMonth,
    months: String(plan.months.length),
    note: plan.note ?? '',
  });
  useEffect(() => {
    setF({
      contractAmount: plan.contractAmount ? String(plan.contractAmount) : '',
      vatRate: String(plan.vatRate),
      financingRate: String(plan.financingRate),
      startMonth: plan.startMonth,
      months: String(plan.months.length),
      note: plan.note ?? '',
    });
  }, [plan]);
  const num = (s: string) => Number(s.replace(/\s/g, '').replace(',', '.'));
  const m = useMutation({
    mutationFn: () =>
      api<FinPlansResponse>(`/fin-plans/${plan.id}`, {
        method: 'PATCH',
        body: {
          contractAmount: f.contractAmount ? num(f.contractAmount) : null,
          vatRate: num(f.vatRate),
          financingRate: num(f.financingRate),
          startMonth: f.startMonth,
          months: Math.round(num(f.months)),
          note: f.note.trim() || null,
        },
      }),
    onSuccess: (r) => {
      onSaved(r);
      toast('Параметры плана сохранены');
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!editable || hasUnsavedRows || m.isPending) return;
    m.mutate();
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label="Сумма доходного договора с НДС, ₸">
        <Input
          inputMode="decimal"
          disabled={!editable}
          value={f.contractAmount}
          onChange={(e) => setF({ ...f, contractAmount: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="НДС, %" hint="выручка без НДС по этой ставке">
          <Input
            inputMode="decimal"
            disabled={!editable}
            value={f.vatRate}
            onChange={(e) => setF({ ...f, vatRate: e.target.value })}
          />
        </Field>
        <Field label="Ставка финансирования, % годовых" hint="плата за отрицательный баланс">
          <Input
            inputMode="decimal"
            disabled={!editable}
            value={f.financingRate}
            onChange={(e) => setF({ ...f, financingRate: e.target.value })}
          />
        </Field>
        <Field label="Первый месяц">
          <Input
            type="month"
            disabled={!editable}
            value={f.startMonth}
            onChange={(e) => setF({ ...f, startMonth: e.target.value })}
          />
        </Field>
        <Field label="Месяцев в плане">
          <Input
            inputMode="numeric"
            disabled={!editable}
            value={f.months}
            onChange={(e) => setF({ ...f, months: e.target.value })}
          />
        </Field>
      </div>
      <Field
        label="Основание и примечание"
        hint="Доходный договор, расчёт себестоимости, график работ и расчётов (Регламент, п. 3.3)"
      >
        <Textarea
          rows={3}
          disabled={!editable}
          value={f.note}
          onChange={(e) => setF({ ...f, note: e.target.value })}
        />
      </Field>
      <ErrorBox error={m.error} />
      {editable && hasUnsavedRows ? (
        <p className="text-[13px] text-warning" role="status">
          Сначала сохраните строки плана
        </p>
      ) : null}
      {editable ? (
        <Button type="submit" disabled={hasUnsavedRows} loading={m.isPending}>
          Сохранить параметры
        </Button>
      ) : null}
    </form>
  );
}

function BgCalculator({
  months,
  editable,
  onAdd,
}: {
  months: string[];
  editable: boolean;
  onAdd: (month: string, fee: number) => void;
}) {
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState('3');
  const [days, setDays] = useState('30');
  const [month, setMonth] = useState(months[0] ?? '');
  const num = (s: string) => Number(s.replace(/\s/g, '').replace(',', '.')) || 0;
  const fee = bankGuaranteeFee(num(amount), num(rate), Math.round(num(days)));
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-ink-2">
        Сумма гарантии × ставка / 365 × дни периода, как в листе «Расходы по БГ» (БМ-20).
      </p>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Сумма БГ, ₸">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Ставка, %">
          <Input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        <Field label="Дней">
          <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
      </div>
      <p className="text-[14px] text-ink">
        Вознаграждение: <b className="num">{tengeShort(fee)}</b>
      </p>
      {editable ? (
        <div className="flex items-end gap-2">
          <Field label="В месяц" className="flex-1">
            <Select value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthTitle(m)}
                </option>
              ))}
            </Select>
          </Field>
          <Button disabled={!fee || !month} onClick={() => onAdd(month, fee)}>
            Добавить в статью БГ
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Диапазон месяцев строки плана: «окт 26 – янв 27» или отдельные месяцы, если их немного */
function monthsSpan(list: string[]): string {
  if (!list.length) return '—';
  if (list.length <= 3) return list.map(monthTitle).join(', ');
  return `${monthTitle(list[0]!)} – ${monthTitle(list[list.length - 1]!)} (${list.length} мес.)`;
}

/**
 * ФП без сумм для ПТО и ГИП (приложение В.1): строки плана, месяцы и объёмы из карточки проекта.
 * На шаге «Объёмы у ПТО» здесь же подтверждение или возврат на доработку.
 */
function FinPlanVolumesView({ project }: { project: ProjectDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const [versionId, setVersionId] = useState<string | undefined>();
  const [returning, setReturning] = useState(false);
  const [comment, setComment] = useState('');
  const q = useQuery({
    queryKey: ['finplan', project.id, 'volumes', versionId ?? 'current'],
    queryFn: () => api<FinPlanVolumes>(`/projects/${project.id}/fin-plans/volumes`, { query: { versionId } }),
  });
  const cur = q.data?.current ?? null;
  const route = useMutation({
    mutationFn: (v: { action: FinRouteAction; comment?: string }) =>
      api<FinPlansResponse>(`/fin-plans/${cur!.id}/route`, { body: v }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['finplan', project.id] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      setReturning(false);
      setComment('');
      toast(v.action === 'return' ? 'План возвращён на доработку' : 'Объёмы подтверждены');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const { versions, volumes } = q.data;
  if (!cur)
    return (
      <EmptyState title="Финансового плана ещё нет">
        План готовит руководитель проекта; на шаге ПТО здесь появятся его строки и объёмы без сумм.
      </EmptyState>
    );
  const myStep = cur.status === 'review_pto' && can(me.access, 'psd.approve', target);
  return (
    <div className="flex flex-col gap-6">
      <Panel title="Финансовый план без сумм" meta="ПТО и ГИП подтверждают объёмы без денег (приложение В.1)">
        <div className="flex flex-wrap items-center gap-2">
          {versions.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVersionId(v.id)}
              className={clsx(
                'inline-flex h-8 items-center gap-2 rounded-md border px-3 text-[13px]',
                v.id === cur.id
                  ? 'border-ink bg-surface text-ink'
                  : 'border-line text-ink-2 hover:border-ink-3',
              )}
            >
              № {v.number}
              <Badge tone={STATUS_TONE[v.status] ?? 'neutral'}>
                {FIN_STATUSES[v.status as keyof typeof FIN_STATUSES]}
              </Badge>
            </button>
          ))}
        </div>
        <ol className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {cur.route.map((s) => (
            <li
              key={s.step}
              className={clsx(
                'rounded-md border px-4 py-3',
                s.at ? 'border-success/40 bg-success-soft/40' : 'border-line',
              )}
            >
              <p className="text-[13px] font-medium text-ink">{s.title}</p>
              <p className="mt-0.5 text-[12px] text-ink-2">
                {s.at ? `${s.by ?? '—'}, ${dateTime(s.at)}` : 'ещё нет'}
              </p>
            </li>
          ))}
        </ol>
        {cur.returnReason && cur.status === 'draft' ? (
          <div className="mt-4 rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
            <b>Возвращён на доработку:</b> {cur.returnReason}
          </div>
        ) : null}
        {myStep ? (
          <div className="mt-4 flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={route.isPending && route.variables?.action === 'pto'}
                onClick={() => route.mutate({ action: 'pto' })}
              >
                {FIN_ROUTE.pto.label}
              </Button>
              <Button variant="ghost" onClick={() => setReturning((v) => !v)}>
                Вернуть на доработку
              </Button>
            </div>
            {returning ? (
              <div className="flex max-w-2xl flex-col gap-2">
                <Textarea
                  rows={2}
                  placeholder="Что не сходится по объёмам"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <div>
                  <Button
                    variant="primary"
                    disabled={!comment.trim()}
                    loading={route.isPending && route.variables?.action === 'return'}
                    onClick={() => route.mutate({ action: 'return', comment })}
                  >
                    Вернуть на доработку
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Panel>
      <Panel title="Объёмы по карточке проекта" meta={`${volumes.length} поз.`} bodyClassName="p-0">
        {volumes.length ? (
          <ScrollX>
            <table className="w-full min-w-[560px] text-[13px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2 pr-3 pl-5 font-medium">Статья</th>
                  <th className="py-2 pr-3 font-medium">Наименование</th>
                  <th className="py-2 pr-5 text-right font-medium">Количество</th>
                </tr>
              </thead>
              <tbody>
                {volumes.map((v, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: строки карточки без id в этом виде
                  <tr key={i} className="border-t border-line">
                    <td className="py-2 pr-3 pl-5 text-ink-2">{v.article}</td>
                    <td className="py-2 pr-3">{v.name}</td>
                    <td className="num py-2 pr-5 text-right whitespace-nowrap">
                      {v.quantity.toLocaleString('ru-RU')} {v.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        ) : (
          <p className="px-5 py-4 text-[14px] text-ink-2">Карточка проекта ещё не заполнена.</p>
        )}
      </Panel>
      <Panel title="Строки плана" meta="без сумм" bodyClassName="p-0">
        <ScrollX>
          <table className="w-full min-w-[560px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2 pr-3 pl-5 font-medium">Раздел</th>
                <th className="py-2 pr-3 font-medium">Строка</th>
                <th className="py-2 pr-5 font-medium">Месяцы плана</th>
              </tr>
            </thead>
            <tbody>
              {cur.rows.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="py-2 pr-3 pl-5 text-ink-2">{FIN_SECTIONS[r.section]}</td>
                  <td className="py-2 pr-3">
                    {r.name}
                    {r.article && r.article !== r.name ? (
                      <span className="text-ink-2"> · {r.article}</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-5 text-ink-2">{monthsSpan(r.activeMonths)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </Panel>
    </div>
  );
}

export function FinPlanTab({ project }: { project: ProjectDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const money = seesMoney(me.access);
  const canEdit = can(me.access, 'budget.edit', target);
  const [versionId, setVersionId] = useState<string | undefined>();
  const q = useQuery({ ...finPlansQuery(project.id, versionId), enabled: money });
  const cur = q.data?.current ?? null;
  const editable = Boolean(cur && cur.status === 'draft' && canEdit);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [showFact, setShowFact] = useState(false);
  const [adding, setAdding] = useState(false);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    if (cur) {
      setRows(toDraft(cur));
      setDirty(false);
    }
  }, [cur]);

  const apply = (r: FinPlansResponse) => {
    qc.setQueryData(finPlansQuery(project.id, r.current?.id).queryKey, r);
    qc.invalidateQueries({ queryKey: ['finplan', project.id] });
    qc.invalidateQueries({ queryKey: ['procurement'] });
    if (r.current) setVersionId(r.current.id);
  };

  const create = useMutation({
    mutationFn: () => api<FinPlansResponse>(`/projects/${project.id}/fin-plans`, { method: 'POST' }),
    onSuccess: (r) => {
      apply(r);
      toast(`Создана версия № ${r.current?.number}`);
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api<FinPlansResponse>(`/fin-plans/${cur!.id}/rows`, {
        method: 'PUT',
        body: {
          rows: rows.map(({ section, article, name, values }) => ({ section, article, name, values })),
        },
      }),
    onSuccess: (r) => {
      apply(r);
      toast('План сохранён, итоги пересчитаны');
    },
  });
  const route = useMutation({
    mutationFn: (v: { action: FinRouteAction; comment?: string }) =>
      api<FinPlansResponse>(`/fin-plans/${cur!.id}/route`, { body: v }),
    onSuccess: (r, v) => {
      apply(r);
      setReturning(false);
      toast(v.action === 'return' ? 'План возвращён на доработку' : FIN_STATUSES[r.current!.status]);
    },
  });

  const months = cur?.months ?? [];
  const sectionTotals = useMemo(() => {
    const out: Record<string, Record<string, number>> = {};
    for (const s of SECTION_ORDER) out[s] = {};
    for (const r of rows) {
      for (const [m, v] of Object.entries(r.values)) out[r.section]![m] = (out[r.section]![m] ?? 0) + v;
    }
    return out;
  }, [rows]);

  if (!money) {
    // ПТО и ГИП подтверждают объёмы без сумм (приложение В.1)
    if (['psd.approve', 'budget.view', 'finance.view'].some((x) => can(me.access, x, target)))
      return <FinPlanVolumesView project={project} />;
    return (
      <EmptyState title="Суммы скрыты">
        Финансовый план виден с уровнем доступа к деньгам «Все деньги».
      </EmptyState>
    );
  }
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;

  const versions = q.data.versions;
  const latest = versions[0];
  const canNewVersion = canEdit && (!latest || ['approved', 'superseded'].includes(latest.status));

  if (!cur)
    return (
      <EmptyState
        title="Финансового плана ещё нет"
        action={
          canEdit ? (
            <div className="flex flex-col items-start gap-3">
              <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
                Создать финансовый план
              </Button>
              <ErrorBox error={create.error} />
            </div>
          ) : undefined
        }
      >
        Форма по приложению 2 к приказу № 29П: закрытие АВР, платежи заказчика, закупки по двенадцати статьям,
        персонал, административные расходы и налоги по месяцам. Портал считает баланс проекта, плату за
        отрицательный баланс, результат и маржу.
      </EmptyState>
    );

  const t = cur.totals;
  const setCell = (key: string, month: string, v: number) => {
    setRows((rs) =>
      rs.map((r) => {
        if (r.key !== key) return r;
        const values = { ...r.values };
        if (v) values[month] = v;
        else delete values[month];
        return { ...r, values };
      }),
    );
    setDirty(true);
  };
  const removeRow = (key: string) => {
    setRows((rs) => rs.filter((r) => r.key !== key));
    setDirty(true);
  };
  const addBg = (month: string, fee: number) => {
    setRows((rs) => {
      const i = rs.findIndex((r) => r.article === BG_ARTICLE);
      if (i < 0)
        return [
          ...rs,
          {
            key: `new-${Date.now()}`,
            section: 'purchase',
            article: BG_ARTICLE,
            name: BG_ARTICLE,
            values: { [month]: fee },
          },
        ];
      return rs.map((r, j) =>
        j === i ? { ...r, values: { ...r.values, [month]: (r.values[month] ?? 0) + fee } } : r,
      );
    });
    setDirty(true);
    toast('Вознаграждение по БГ добавлено в план; сохраните изменения');
  };
  const factOf = (key: string) => cur.rows.find((r) => r.id === key);
  const rowTotal = (r: DraftRow) => months.reduce((s, m) => s + (r.values[m] ?? 0), 0);
  const costsByMonth = (m: string) =>
    FIN_COST_SECTIONS.reduce((s, sec) => s + (sectionTotals[sec]?.[m] ?? 0), 0);
  const actions = (Object.keys(FIN_ROUTE) as FinRouteAction[]).filter(
    (a) =>
      FIN_ROUTE[a].from.includes(cur.status) && can(me.access, finRoutePermission(a, cur.status), target),
  );
  const unplannedRevenue = cur.contractAmount !== null ? cur.contractAmount - t.revenue : null;
  const colCount = months.length + 3;

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Финансовый план"
        meta="приложение 2 к приказу № 29П"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/cost-articles" className="text-[13px] text-ink-2 hover:text-ink hover:underline">
              Статьи расходов
            </Link>
            {canNewVersion ? (
              <Button loading={create.isPending} onClick={() => create.mutate()}>
                Новая версия
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          {versions.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVersionId(v.id)}
              className={clsx(
                'inline-flex h-8 items-center gap-2 rounded-md border px-3 text-[13px]',
                v.id === cur.id
                  ? 'border-ink bg-surface text-ink'
                  : 'border-line text-ink-2 hover:border-ink-3',
              )}
            >
              № {v.number}
              <Badge tone={STATUS_TONE[v.status] ?? 'neutral'}>
                {FIN_STATUSES[v.status as keyof typeof FIN_STATUSES]}
              </Badge>
            </button>
          ))}
        </div>
        <ol className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {cur.route.map((s) => (
            <li
              key={s.step}
              className={clsx(
                'rounded-md border px-4 py-3',
                s.at ? 'border-success/40 bg-success-soft/40' : 'border-line',
              )}
            >
              <p className="text-[13px] font-medium text-ink">{s.title}</p>
              <p className="mt-0.5 text-[12px] text-ink-2">
                {s.at ? `${s.by ?? '—'}, ${dateTime(s.at)}` : 'ещё нет'}
              </p>
            </li>
          ))}
        </ol>
        {cur.returnReason && cur.status === 'draft' ? (
          <div className="mt-4 rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
            <b>Возвращён на доработку:</b> {cur.returnReason}
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {actions
            .filter((a) => a !== 'return')
            .map((a) => (
              <Button
                key={a}
                variant="primary"
                disabled={dirty}
                loading={route.isPending && route.variables?.action === a}
                onClick={() => route.mutate({ action: a })}
              >
                {FIN_ROUTE[a].label}
              </Button>
            ))}
          {actions.includes('return') ? (
            <Button variant="ghost" onClick={() => setReturning(true)}>
              Вернуть на доработку
            </Button>
          ) : null}
          {cur.status === 'approved' ? (
            <span className="text-[13px] text-ink-2">
              Действует с момента утверждения. Изменение суммы, сроков, объёмов, цен, численности или налогов
              — новая версия (БМ-21).
            </span>
          ) : null}
          {dirty ? (
            <span className="text-[13px] text-warning">Сохраните изменения, чтобы отправить план дальше</span>
          ) : null}
        </div>
        <ErrorBox error={route.error} className="mt-3" />
      </Panel>

      <TitleBlock
        items={[
          {
            label: 'Доходный договор',
            value: tengeShort(cur.contractAmount),
            note:
              unplannedRevenue === null
                ? 'сумма договора не внесена'
                : Math.abs(unplannedRevenue) >= 1
                  ? `${unplannedRevenue > 0 ? 'не распределено' : 'платежей больше договора на'} ${tengeShort(Math.abs(unplannedRevenue))}`
                  : 'платежи заказчика распределены',
          },
          {
            label: 'Платежи заказчика',
            value: tengeShort(t.revenue),
            note: `без НДС ${tengeShort(t.revenueNet)}; факт ${tengeShort(t.revenueFact)}`,
          },
          { label: 'Расходы', value: tengeShort(t.costs), note: `факт ${tengeShort(t.costsFact)}` },
          {
            label: 'Плата за деньги',
            value: tengeShort(t.costOfMoney),
            note: `минимальный баланс ${tengeShort(t.minBalance)}`,
            tone: t.costOfMoney > 0 ? 'danger' : 'default',
          },
          {
            label: 'Финансовый результат',
            value: tengeShort(t.result),
            note: `маржа ${percent(t.marginPct)}`,
            tone: t.result < 0 ? 'danger' : 'success',
          },
        ]}
      />

      <FinPlanBaselinePanel
        projectId={project.id}
        planId={cur.id}
        stamp={`${cur.status}|${t.result}|${t.costs}`}
      />

      <Panel
        title="План по месяцам, млн ₸"
        meta={editable ? 'суммы с НДС, как денежный поток (БМ-16)' : FIN_STATUSES[cur.status]}
        bodyClassName="p-0"
        actions={
          <>
            <label className="flex items-center gap-2 text-[13px] text-ink-2">
              <input type="checkbox" checked={showFact} onChange={(e) => setShowFact(e.target.checked)} />
              план и факт
            </label>
            {editable ? (
              <>
                <Button variant="ghost" onClick={() => setAdding(true)}>
                  <Plus className="size-4" /> Строка
                </Button>
                <Button
                  variant="primary"
                  disabled={!dirty}
                  loading={save.isPending}
                  onClick={() => save.mutate()}
                >
                  Сохранить
                </Button>
              </>
            ) : null}
          </>
        }
      >
        <ErrorBox error={save.error} className="m-4" />
        <div className="max-h-[72vh] overflow-auto">
          <table className="text-[13px]">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="text-left text-[12px] text-ink-2">
                <th className="sticky left-0 z-20 min-w-[280px] bg-surface-2 py-2.5 pr-3 pl-5 font-medium">
                  Статья
                </th>
                {months.map((m) => (
                  <th
                    key={m}
                    className="min-w-[92px] border-l border-line px-2 py-2.5 text-right font-medium whitespace-nowrap"
                  >
                    {monthTitle(m)}
                  </th>
                ))}
                <th className="min-w-[96px] border-l border-line px-2 py-2.5 text-right font-medium">
                  Итого
                </th>
                <th className="min-w-[96px] px-2 py-2.5 pr-5 text-right font-medium">Факт</th>
              </tr>
            </thead>
            <tbody>
              {SECTION_ORDER.map((sec) => {
                const list = rows.filter((r) => r.section === sec);
                const secTotal = months.reduce((s, m) => s + (sectionTotals[sec]?.[m] ?? 0), 0);
                const secFact = cur.rows
                  .filter((r) => r.section === sec)
                  .reduce((s, r) => s + r.factTotal, 0);
                return [
                  <tr key={sec} className="border-t border-line-strong bg-surface-2/60">
                    <td className="sticky left-0 z-[1] bg-surface-2 py-2 pr-3 pl-5 font-semibold text-ink">
                      {FIN_SECTIONS[sec]}
                    </td>
                    {months.map((m) => (
                      <td key={m} className="num border-l border-line px-2 py-2 text-right font-semibold">
                        {sectionTotals[sec]?.[m] ? mln(sectionTotals[sec]![m]) : ''}
                      </td>
                    ))}
                    <td className="num border-l border-line px-2 py-2 text-right font-semibold">
                      {mln(secTotal)}
                    </td>
                    <td className="num px-2 py-2 pr-5 text-right font-semibold text-ink-2">
                      {secFact ? mln(secFact) : ''}
                    </td>
                  </tr>,
                  ...list.map((r) => {
                    const f = factOf(r.key);
                    return (
                      <tr key={r.key} className="border-t border-line">
                        <td className="sticky left-0 z-[1] bg-surface py-1.5 pr-3 pl-9">
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1">
                              {r.name}
                              {r.article && r.article !== r.name ? (
                                <span className="block text-[12px] text-ink-2">{r.article}</span>
                              ) : null}
                            </span>
                            {editable ? (
                              <IconButton
                                label={`Удалить строку «${r.name}»`}
                                onClick={() => removeRow(r.key)}
                              >
                                <Trash2 className="size-4" />
                              </IconButton>
                            ) : null}
                          </div>
                        </td>
                        {months.map((m) => {
                          const v = r.values[m];
                          const fv = f?.fact[m];
                          return (
                            <td key={m} className="num border-l border-line px-1 py-1 text-right align-top">
                              {editable ? (
                                <FinPlanAmountInput
                                  key={`${cur.id}-${r.key}-${m}-${v ?? 0}`}
                                  value={v}
                                  label={`${r.name}, ${monthTitle(m)}, млн ₸`}
                                  onCommit={(next) => setCell(r.key, m, next)}
                                />
                              ) : (
                                <span className={clsx('block px-1 py-1', !v && 'text-ink-3')}>
                                  {v ? mln(v) : '·'}
                                </span>
                              )}
                              {showFact && fv ? (
                                <span
                                  className={clsx(
                                    'block px-1 text-[11px]',
                                    FIN_COST_SECTIONS.includes(r.section) && fv > (v ?? 0)
                                      ? 'text-danger'
                                      : 'text-ink-2',
                                  )}
                                  title="факт"
                                >
                                  ф {mln(fv)}
                                </span>
                              ) : null}
                            </td>
                          );
                        })}
                        <td className="num border-l border-line px-2 py-1.5 text-right">
                          {mln(rowTotal(r))}
                        </td>
                        <td className="num px-2 py-1.5 pr-5 text-right text-ink-2">
                          {f?.factTotal ? mln(f.factTotal) : ''}
                        </td>
                      </tr>
                    );
                  }),
                ];
              })}
              <tr className="border-t-2 border-ink/30 bg-surface-2/60">
                <td className="sticky left-0 z-[1] bg-surface-2 py-2 pr-3 pl-5 font-semibold">
                  Расходы всего
                </td>
                {months.map((m) => (
                  <td key={m} className="num border-l border-line px-2 py-2 text-right font-semibold">
                    {mln(costsByMonth(m))}
                  </td>
                ))}
                <td className="num border-l border-line px-2 py-2 text-right font-semibold">
                  {mln(months.reduce((s, m) => s + costsByMonth(m), 0))}
                </td>
                <td className="num px-2 py-2 pr-5 text-right text-ink-2">
                  {t.costsFact ? mln(t.costsFact) : ''}
                </td>
              </tr>
              <tr className="border-t border-line">
                <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5">Движение денег за месяц</td>
                {months.map((m) => {
                  const v = (sectionTotals.revenue?.[m] ?? 0) - costsByMonth(m);
                  return (
                    <td
                      key={m}
                      className={clsx(
                        'num border-l border-line px-2 py-2 text-right',
                        v < 0 && 'text-danger',
                      )}
                    >
                      {mln(v)}
                    </td>
                  );
                })}
                <td className="border-l border-line" colSpan={2} />
              </tr>
              <tr className="border-t border-line">
                <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5">
                  Баланс на конец месяца
                  {dirty ? (
                    <span className="block text-[12px] text-warning">пересчитается после сохранения</span>
                  ) : null}
                </td>
                {months.map((m) => {
                  const v = t.balance[m] ?? 0;
                  return (
                    <td
                      key={m}
                      className={clsx(
                        'num border-l border-line px-2 py-2 text-right font-medium',
                        v < 0 ? 'text-danger' : 'text-ink',
                      )}
                    >
                      {mln(v)}
                    </td>
                  );
                })}
                <td className="border-l border-line" colSpan={2} />
              </tr>
              <tr className="border-t border-line">
                <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5">
                  Плата за отрицательный баланс, {String(cur.financingRate).replace('.', ',')} % годовых
                </td>
                {months.map((m) => (
                  <td key={m} className="num border-l border-line px-2 py-2 text-right text-ink-2">
                    {t.costOfMoneyByMonth[m] ? mln(t.costOfMoneyByMonth[m]) : ''}
                  </td>
                ))}
                <td className="num border-l border-line px-2 py-2 text-right">{mln(t.costOfMoney)}</td>
                <td />
              </tr>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-5 py-6 text-[14px] text-ink-2">
                    Строк нет — добавьте платежи заказчика и расходы по статьям.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Panel
          title="Статьи: план, договоры, оплаты, остаток"
          meta="остаток ограничивает закупки (ЗК-29)"
          bodyClassName="p-0"
        >
          {cur.articles.length ? (
            <ScrollX>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2.5 pr-3 pl-5 font-medium">Статья</th>
                    <th className="px-2 py-2.5 text-right font-medium">План</th>
                    <th className="px-2 py-2.5 text-right font-medium">Договоры</th>
                    <th className="px-2 py-2.5 text-right font-medium">Оплачено</th>
                    <th className="px-2 py-2.5 pr-5 text-right font-medium">Остаток</th>
                  </tr>
                </thead>
                <tbody>
                  {cur.articles.map((a) => (
                    <tr key={a.article} className="border-t border-line">
                      <td className="py-2 pr-3 pl-5">{a.article}</td>
                      <td className="num px-2 py-2 text-right">{mln(a.plan)}</td>
                      <td className="num px-2 py-2 text-right">{mln(a.contracted)}</td>
                      <td className="num px-2 py-2 text-right">{mln(a.paid)}</td>
                      <td
                        className={clsx(
                          'num px-2 py-2 pr-5 text-right font-medium',
                          a.remainder < 0 ? 'text-danger' : 'text-ink',
                        )}
                      >
                        {mln(a.remainder)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          ) : (
            <p className="p-5 text-[14px] text-ink-2">По статьям пока нет ни плана, ни договоров.</p>
          )}
        </Panel>
        <div className="flex flex-col gap-6">
          <Panel title="Параметры плана">
            <FinPlanSettings plan={cur} editable={editable} hasUnsavedRows={dirty} onSaved={apply} />
          </Panel>
          <Panel title="Вознаграждение по банковской гарантии">
            <BgCalculator months={months} editable={editable} onAdd={addBg} />
          </Panel>
        </div>
      </div>

      {adding ? (
        <AddRowDialog
          onClose={() => setAdding(false)}
          onAdd={(r) => {
            setRows((rs) => {
              const last = rs.map((x) => x.section).lastIndexOf(r.section);
              const out = [...rs];
              out.splice(last < 0 ? out.length : last + 1, 0, r);
              return out;
            });
            setDirty(true);
          }}
        />
      ) : null}
      {returning ? (
        <ReturnDialog
          pending={route.isPending}
          onClose={() => setReturning(false)}
          onSend={(comment) => route.mutate({ action: 'return', comment })}
        />
      ) : null}
    </div>
  );
}
